import { useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";
import type { ProductCard, SolutionCard } from "@itaas/pep-contracts";
import { ProductLogo } from "../../components/catalog/ProductLogo";
import {
  labelStatus,
  productDisplayName,
  productTagline,
  solutionDisplayName,
  statusBadgeClass,
} from "../../utils/labels";
import { CatalogHoverTip } from "./CatalogHoverTip";
import { CatalogInfoButton } from "./CatalogInfoButton";
import {
  TrialCatalogDetailModal,
  type CatalogDetailTarget,
} from "./TrialCatalogDetailModal";

type PickerView = "solutions" | "modules";

interface TrialModulePickerProps {
  products: ProductCard[];
  solutions: SolutionCard[];
  selected: string[];
  onToggle: (slug: string) => void;
  onSetSelected: (slugs: string[] | ((prev: string[]) => string[])) => void;
}

function trialProductsForSolution(solution: SolutionCard, products: ProductCard[]): ProductCard[] {
  const bySlug = new Map(products.map((p) => [p.slug, p]));
  return solution.products.map((slug) => bySlug.get(slug)).filter(Boolean) as ProductCard[];
}

export function TrialModulePicker({
  products,
  solutions,
  selected,
  onToggle,
  onSetSelected,
}: TrialModulePickerProps) {
  const [view, setView] = useState<PickerView>("solutions");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState<CatalogDetailTarget | null>(null);

  useEffect(() => {
    if (!detail) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [detail]);

  const eligibleSolutions = useMemo(
    () => solutions.filter((s) => trialProductsForSolution(s, products).length > 0),
    [solutions, products],
  );

  const productFuse = useMemo(
    () =>
      new Fuse(products, {
        keys: [
          { name: "slug", weight: 0.3 },
          { name: "displayName", weight: 0.5 },
          { name: "displayNameFr", weight: 0.5 },
          { name: "taglineFr", weight: 0.2 },
          { name: "descriptionFr", weight: 0.15 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [products],
  );

  const solutionFuse = useMemo(
    () =>
      new Fuse(eligibleSolutions, {
        keys: [
          { name: "slug", weight: 0.2 },
          { name: "displayName", weight: 0.4 },
          { name: "displayNameFr", weight: 0.4 },
          { name: "descriptionFr", weight: 0.25 },
          { name: "longDescriptionFr", weight: 0.15 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [eligibleSolutions],
  );

  const filteredProducts = useMemo(() => {
    const q = query.trim();
    if (!q) return products;
    return productFuse.search(q).map((r) => r.item);
  }, [products, query, productFuse]);

  const filteredSolutions = useMemo(() => {
    const q = query.trim();
    if (!q) return eligibleSolutions;
    return solutionFuse.search(q).map((r) => r.item);
  }, [eligibleSolutions, query, solutionFuse]);

  function toggleSolution(solution: SolutionCard) {
    const slugs = trialProductsForSolution(solution, products).map((p) => p.slug);
    onSetSelected((current) => {
      const allSelected = slugs.length > 0 && slugs.every((slug) => current.includes(slug));
      if (allSelected) {
        return current.filter((s) => !slugs.includes(s));
      }
      return [...new Set([...current, ...slugs])];
    });
  }

  function isDetailSelected(target: CatalogDetailTarget): boolean {
    if (target.kind === "product") {
      return selected.includes(target.product.slug);
    }
    const slugs = target.products.map((p) => p.slug);
    return slugs.length > 0 && slugs.every((slug) => selected.includes(slug));
  }

  function toggleDetailSelect() {
    if (!detail) return;
    if (detail.kind === "product") {
      onToggle(detail.product.slug);
      return;
    }
    toggleSolution(detail.solution);
  }

  function selectAllVisible() {
    if (view === "modules") {
      onSetSelected(filteredProducts.map((p) => p.slug));
      return;
    }
    const slugs = filteredSolutions.flatMap((s) =>
      trialProductsForSolution(s, products).map((p) => p.slug),
    );
    onSetSelected([...new Set(slugs)]);
  }

  function clearAll() {
    onSetSelected([]);
  }

  const visibleCount = view === "solutions" ? filteredSolutions.length : filteredProducts.length;
  const totalCount = view === "solutions" ? eligibleSolutions.length : products.length;

  return (
    <div className="pep-trial-modules">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="pep-trial-view-toggle btn-group btn-group-sm" role="group" aria-label="Mode d'affichage">
          <button
            type="button"
            className={`btn ${view === "solutions" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setView("solutions")}
            aria-pressed={view === "solutions"}
          >
            Solutions
          </button>
          <button
            type="button"
            className={`btn ${view === "modules" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setView("modules")}
            aria-pressed={view === "modules"}
          >
            Modules
          </button>
        </div>
        <div className="flex-grow-1" style={{ minWidth: "10rem" }}>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder={view === "solutions" ? "Rechercher une solution…" : "Rechercher un module…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={view === "solutions" ? "Rechercher une solution" : "Rechercher un module"}
          />
        </div>
        <span className="badge text-bg-secondary">
          {selected.length} module{selected.length > 1 ? "s" : ""} · {visibleCount}/{totalCount}
        </span>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={selectAllVisible}>
          Tout
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearAll}>
          Aucun
        </button>
      </div>

      {view === "solutions" && (
        <div
          className="pep-trial-modules__grid pep-trial-modules__grid--solutions"
          role="group"
          aria-label="Solutions disponibles"
        >
          {filteredSolutions.length === 0 && (
            <p className="text-secondary small mb-0 p-3 col-span-all">
              Aucune solution ne correspond à votre recherche.
            </p>
          )}
          {filteredSolutions.map((s) => {
            const included = trialProductsForSolution(s, products);
            const slugs = included.map((p) => p.slug);
            const selectedCount = slugs.filter((slug) => selected.includes(slug)).length;
            const fullySelected = slugs.length > 0 && selectedCount === slugs.length;
            const partiallySelected = selectedCount > 0 && !fullySelected;
            const heroSlug = included[0]?.slug;

            return (
              <div
                key={s.slug}
                className={`pep-trial-module-chip pep-trial-solution-chip${fullySelected ? " is-selected" : ""}${partiallySelected ? " is-partial" : ""}`}
              >
                <CatalogHoverTip
                  label={solutionDisplayName(s)}
                  text={s.descriptionFr ?? s.description}
                >
                  <button
                    type="button"
                    className="pep-trial-chip-main"
                    onClick={() => toggleSolution(s)}
                    aria-pressed={fullySelected}
                  >
                    {heroSlug && (
                      <ProductLogo
                        slug={heroSlug}
                        name={solutionDisplayName(s)}
                        size={28}
                        className="pep-trial-chip-logo flex-shrink-0"
                      />
                    )}
                    <span className="pep-trial-module-chip__check" aria-hidden>
                      {fullySelected ? "✓" : partiallySelected ? "−" : ""}
                    </span>
                    <span className="pep-trial-module-chip__body">
                      <span className="pep-trial-module-chip__name">{solutionDisplayName(s)}</span>
                      <span className="pep-trial-module-chip__meta">
                        <span className="text-secondary">
                          {slugs.length} module{slugs.length > 1 ? "s" : ""}
                          {partiallySelected ? ` · ${selectedCount}/${slugs.length}` : ""}
                        </span>
                      </span>
                    </span>
                  </button>
                </CatalogHoverTip>
                <CatalogInfoButton
                  label={solutionDisplayName(s)}
                  onClick={() =>
                    setDetail({ kind: "solution", solution: s, products: included })
                  }
                />
              </div>
            );
          })}
        </div>
      )}

      {view === "modules" && (
        <div className="pep-trial-modules__grid" role="group" aria-label="Modules disponibles">
          {filteredProducts.length === 0 && (
            <p className="text-secondary small mb-0 p-3">Aucun module ne correspond à votre recherche.</p>
          )}
          {filteredProducts.map((p) => {
            const active = selected.includes(p.slug);

            return (
              <div
                key={p.slug}
                className={`pep-trial-module-chip${active ? " is-selected" : ""}`}
              >
                <CatalogHoverTip label={productDisplayName(p)} text={productTagline(p)}>
                  <button
                    type="button"
                    className="pep-trial-chip-main"
                    onClick={() => onToggle(p.slug)}
                    aria-pressed={active}
                  >
                    <ProductLogo
                      slug={p.slug}
                      name={productDisplayName(p)}
                      size={28}
                      className="pep-trial-chip-logo flex-shrink-0"
                    />
                    <span className="pep-trial-module-chip__check" aria-hidden>
                      {active ? "✓" : ""}
                    </span>
                    <span className="pep-trial-module-chip__body">
                      <span className="pep-trial-module-chip__name">{productDisplayName(p)}</span>
                      <span className="pep-trial-module-chip__meta">
                        <span className={`badge ${statusBadgeClass(p.status)}`}>
                          {labelStatus(p.status)}
                        </span>
                        {p.trialDays ? (
                          <span className="text-secondary">{p.trialDays} j</span>
                        ) : null}
                      </span>
                    </span>
                  </button>
                </CatalogHoverTip>
                <CatalogInfoButton
                  label={productDisplayName(p)}
                  onClick={() => setDetail({ kind: "product", product: p })}
                />
              </div>
            );
          })}
        </div>
      )}

      <TrialCatalogDetailModal
        target={detail}
        selected={detail ? isDetailSelected(detail) : false}
        onClose={() => setDetail(null)}
        onToggleSelect={toggleDetailSelect}
      />
    </div>
  );
}
